import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiUrl, apiUnreachableMessage } from '../config/api';
import { getStoredUser } from '../utils/auth';
import { isAboveSupervisorRole } from '../utils/roles';

function formatDate(value) {
  const text = String(value || '').trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return text || '—';
  return `${match[3]}/${match[2]}/${match[1]}`;
}

function BellIcon({ className = 'h-7 w-7' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden>
      <path
        d="M6.5 9.5a5.5 5.5 0 0 1 11 0c0 3.2.84 4.35 1.55 5.15A1 1 0 0 1 18.3 16.5H5.7a1 1 0 0 1-.75-1.85C5.66 13.85 6.5 12.7 6.5 9.5Z"
        strokeLinejoin="round"
      />
      <path d="M10 16.5a2 2 0 0 0 4 0" strokeLinecap="round" />
    </svg>
  );
}

function InfoIcon({ className = 'h-4 w-4' }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className={className} aria-hidden>
      <circle cx="10" cy="10" r="7" />
      <path d="M10 9v5" strokeLinecap="round" />
      <circle cx="10" cy="6.25" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

function statusLabel(item) {
  const days = Number(item?.days_until_expiry);
  if (item?.status === 'expired' || days < 0) {
    const since = Math.abs(days);
    return `Expired ${since} day${since === 1 ? '' : 's'} ago`;
  }
  if (days === 0) return 'Expires today';
  return `Expires in ${days} day${days === 1 ? '' : 's'}`;
}

function notificationParts(item) {
  const certName = String(item.certificate_name || 'Certificate').trim() || 'Certificate';
  const person = String(item.employee_name || item.employee_email || 'an employee').trim();
  const expiry = formatDate(item.expires_on);
  const days = Number(item?.days_until_expiry);
  const expired = item?.status === 'expired' || days < 0;
  if (expired) {
    return {
      mode: 'expired',
      certName,
      person,
      expiry,
      sentence: `${certName} for ${person} expired on ${expiry}.`,
    };
  }
  if (days === 0) {
    return {
      mode: 'today',
      certName,
      person,
      expiry,
      sentence: `${certName} for ${person} expires today (${expiry}).`,
    };
  }
  return {
    mode: 'upcoming',
    certName,
    person,
    expiry,
    sentence: `${certName} for ${person} expires on ${expiry}.`,
  };
}

function NotificationInfoPopup({ item, onClose }) {
  const rows = [
    { label: 'Certificate name', value: item.certificate_name || '—' },
    { label: 'Status', value: statusLabel(item) },
    { label: 'Employee', value: item.employee_name || '—' },
    { label: 'Company', value: item.company_name || '—' },
    { label: 'Employee email', value: item.employee_email || '—' },
    { label: 'Issue date', value: formatDate(item.issued_on) },
    { label: 'Expiry date', value: formatDate(item.expires_on) },
    { label: 'Description', value: String(item.description || '').trim() || '—' },
  ];

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-3"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-sm rounded-lg border border-gray-700 bg-gray-950 p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${item.certificate_name} notification details`}
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <h4 className="text-sm font-semibold text-white">Notification info</h4>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xl leading-none text-gray-400 hover:bg-white/10 hover:text-white"
            aria-label="Close"
            title="Close"
          >
            ×
          </button>
        </div>
        <dl className="space-y-3">
          {rows.map((row) => (
            <div key={row.label}>
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                {row.label}
              </dt>
              <dd className="mt-1 whitespace-pre-wrap break-words text-[12px] text-gray-200">
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

function NotificationRow({ item, onOpen }) {
  const [infoOpen, setInfoOpen] = useState(false);
  const parts = notificationParts(item);
  const emphasisClass = 'font-bold text-[14px] text-white';

  const openCertificates = () => {
    onOpen?.(item);
  };

  return (
    <li className="px-3 py-2.5">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setInfoOpen(true);
          }}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sky-300 hover:bg-sky-400/10 hover:text-sky-200"
          aria-label={`Info for ${item.certificate_name}`}
          title="Notification info"
        >
          <InfoIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={openCertificates}
          className="min-w-0 flex-1 whitespace-normal break-words text-left text-[13px] leading-5 text-gray-300 hover:text-gray-100"
          title={`${parts.sentence} Open certificates`}
        >
          {parts.mode === 'today' ? (
            <>
              <span className={emphasisClass}>{parts.certName}</span>
              {' for '}
              {parts.person}
              {' expires today ('}
              <span className={emphasisClass}>{parts.expiry}</span>
              {').'}
            </>
          ) : parts.mode === 'expired' ? (
            <>
              <span className={emphasisClass}>{parts.certName}</span>
              {' for '}
              {parts.person}
              {' expired on '}
              <span className={emphasisClass}>{parts.expiry}</span>
              {'.'}
            </>
          ) : (
            <>
              <span className={emphasisClass}>{parts.certName}</span>
              {' for '}
              {parts.person}
              {' expires on '}
              <span className={emphasisClass}>{parts.expiry}</span>
              {'.'}
            </>
          )}
        </button>
      </div>
      {infoOpen ? <NotificationInfoPopup item={item} onClose={() => setInfoOpen(false)} /> : null}
    </li>
  );
}

export function CertificateNotificationsBell({
  className = '',
  buttonClassName = '',
  onOpenCertificate,
}) {
  const user = getStoredUser();
  const enabled = isAboveSupervisorRole(user?.role);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const rootRef = useRef(null);

  const loadNotifications = useCallback(async ({ silent = false } = {}) => {
    if (!enabled || !user?.email) return;
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const res = await fetch(apiUrl('/api/users/certificate-notifications'), {
        headers: { 'X-User-Email': user.email },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (!silent) {
          setError(
            typeof data.detail === 'string' ? data.detail : 'Failed to load notifications'
          );
          setItems([]);
        }
        return;
      }
      setItems(Array.isArray(data.notifications) ? data.notifications : []);
      if (!silent) setError('');
    } catch {
      if (!silent) {
        setError(apiUnreachableMessage());
        setItems([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [enabled, user?.email]);

  useEffect(() => {
    if (!enabled) return undefined;
    loadNotifications();
    const timer = window.setInterval(() => {
      loadNotifications({ silent: true });
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [enabled, loadNotifications]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleClearAll = async () => {
    if (!user?.email || clearing) return;
    const previousItems = items;
    setClearing(true);
    setError('');
    setItems([]);
    try {
      const res = await fetch(apiUrl('/api/users/certificate-notifications'), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user.email,
        },
        body: JSON.stringify({ notify: false }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setItems(previousItems);
        setError(
          typeof data.detail === 'string' ? data.detail : 'Failed to clear notifications'
        );
        return;
      }
      setItems(Array.isArray(data.notifications) ? data.notifications : []);
    } catch {
      setItems(previousItems);
      setError(apiUnreachableMessage());
    } finally {
      setClearing(false);
    }
  };

  if (!enabled) return null;

  const count = items.length;

  return (
    <div className={`relative inline-flex items-center ${className}`} ref={rootRef}>
      <button
        type="button"
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) {
            // Keep current list visible; refresh quietly in the background.
            loadNotifications({ silent: items.length > 0 });
          }
        }}
        className={`relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded text-white hover:bg-white/10 lg:h-10 lg:w-10 ${buttonClassName}`}
        aria-label="Certificate notifications"
        title="Certificate notifications"
        aria-expanded={open}
      >
        <BellIcon className="h-6 w-6 lg:h-7 lg:w-7" />
        {count > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-black lg:h-5 lg:min-w-[1.25rem]">
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="fixed inset-x-3 top-16 z-[80] overflow-hidden rounded-lg border border-gray-700 bg-gray-950 shadow-xl lg:absolute lg:inset-x-auto lg:right-0 lg:top-full lg:mt-2 lg:w-[min(28rem,calc(100vw-1.5rem))]"
          role="dialog"
          aria-label="Certificate notifications"
        >
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-800 px-3 py-2">
            <h4 className="text-sm font-semibold text-white max-lg:basis-full lg:min-w-0 lg:truncate">
              Certificate notifications
            </h4>
            <div className="flex shrink-0 items-center gap-1.5">
              {count > 0 ? (
                <button
                  type="button"
                  onClick={handleClearAll}
                  disabled={clearing}
                  className="rounded-md border border-gray-600 bg-gray-900 px-5 py-1 text-xs font-semibold text-gray-200 hover:bg-gray-800 hover:text-white disabled:opacity-60"
                >
                  {clearing ? 'Clearing…' : 'Clear all'}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xl leading-none text-gray-400 hover:bg-white/10 hover:text-white"
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </div>
          </div>

          <div className="max-h-[min(24rem,60vh)] overflow-y-auto">
            {loading && count === 0 ? (
              <p className="px-3 py-4 text-sm text-gray-500">Loading notifications…</p>
            ) : null}
            {!loading && error && count === 0 ? (
              <p className="px-3 py-4 text-sm text-red-400">{error}</p>
            ) : null}
            {error && count > 0 ? (
              <p className="px-3 py-2 text-[11px] text-red-400">{error}</p>
            ) : null}
            {!loading && !error && count === 0 ? (
              <p className="px-3 py-4 text-sm text-gray-500">
                No certificate expiry notifications right now.
              </p>
            ) : null}
            {count > 0 ? (
              <ul className="divide-y divide-gray-800">
                {items.map((item) => (
                  <NotificationRow
                    key={item.id}
                    item={item}
                    onOpen={(notification) => {
                      setOpen(false);
                      onOpenCertificate?.(notification);
                    }}
                  />
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default CertificateNotificationsBell;
